import { Fragment } from "react";
import type { Annotation, Language } from "../data/types.js";
import type { Strings } from "../lib/strings.js";
import { t } from "../lib/strings.js";

interface Props {
  text: string;
  annotations: Annotation[];
  lang: Language;
  /** Render with one block per `\n`-delimited line and a CSS-counter gutter. */
  lineNumbers?: boolean;
}

/**
 * Renders canonical text with standoff annotations applied as visual markers.
 * Annotations here are scholarly markup of features in the source text —
 * author marks from the v1 backfill (crossed_out, illegible, uncertain,
 * inserted, arrow, drawing) plus future scholarly types (persName, placeName,
 * date, foreign, note). Editorial corrections are NOT annotations — they're
 * direct edits to the canonical text, tracked via transcription_revisions.
 *
 * With lineNumbers=true, each `\n`-delimited canonical line becomes its own
 * block element and a CSS counter renders the line number in the gutter
 * via ::before. The "\n" characters stay as text nodes between blocks so the
 * DOM textContent stays byte-identical to the canonical text — that's what
 * any future selection-offset math relies on.
 */
export function AnnotationLayer({
  text,
  annotations,
  lang,
  lineNumbers = true,
}: Props): JSX.Element {
  const s = t(lang);

  if (!lineNumbers || !text.includes("\n")) {
    return <>{renderRange(text, annotations, s, "n")}</>;
  }

  const lines = splitIntoLines(text);
  return (
    <div className="line-numbered">
      {lines.map((line, i) => {
        const lineAnns = clampToLine(annotations, line);
        return (
          <Fragment key={`L${i}`}>
            {/* keep "\n" in DOM textContent so selection offsets match canonical */}
            {i > 0 && "\n"}
            <span className="line">{renderRange(line.content, lineAnns, s, `L${i}`)}</span>
          </Fragment>
        );
      })}
    </div>
  );
}

interface LineRange {
  start: number;
  end: number;
  content: string;
}

function splitIntoLines(text: string): LineRange[] {
  const out: LineRange[] = [];
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === "\n") {
      out.push({ start, end: i, content: text.slice(start, i) });
      start = i + 1;
    }
  }
  out.push({ start, end: text.length, content: text.slice(start) });
  return out;
}

function clampToLine(annotations: Annotation[], line: LineRange): Annotation[] {
  const result: Annotation[] = [];
  for (const a of annotations) {
    const isZeroWidth = a.startOffset === a.endOffset;
    if (isZeroWidth) {
      if (a.startOffset >= line.start && a.startOffset <= line.end) {
        result.push({
          ...a,
          startOffset: a.startOffset - line.start,
          endOffset: a.endOffset - line.start,
        });
      }
      continue;
    }
    if (a.endOffset <= line.start || a.startOffset >= line.end) continue;
    result.push({
      ...a,
      startOffset: Math.max(0, a.startOffset - line.start),
      endOffset: Math.min(line.end - line.start, a.endOffset - line.start),
    });
  }
  return result;
}

function renderRange(
  text: string,
  annotations: Annotation[],
  s: Strings,
  keyPrefix: string,
): JSX.Element[] {
  const sorted = [...annotations].sort((a, b) => a.startOffset - b.startOffset);

  const nodes: Array<string | JSX.Element> = [];
  let cursor = 0;

  sorted.forEach((a, i) => {
    if (a.startOffset > cursor) nodes.push(text.slice(cursor, a.startOffset));
    const span = text.slice(a.startOffset, a.endOffset);
    const key = `${keyPrefix}-a${i}`;
    switch (a.type) {
      case "crossed_out":
        nodes.push(
          <s key={key} title={s.markCrossedOut} className="decoration-ink-muted">
            {span}
          </s>,
        );
        break;
      case "illegible":
        nodes.push(
          <span key={key} title={s.markIllegible} className="text-ink-muted">
            {span}
          </span>,
        );
        break;
      case "uncertain":
        nodes.push(
          <span key={key} title={s.markUncertain}>
            <i>{span}</i>
            <span className="text-ink-muted">⟨?⟩</span>
          </span>,
        );
        break;
      case "inserted":
        nodes.push(
          <span key={key} title={s.markInserted} className="text-ink-muted">
            ⟨<span className="text-ink">↑ {span}</span>⟩
          </span>,
        );
        break;
      case "arrow":
        nodes.push(
          <span key={key} title={s.markArrow}>
            <span className="text-ink-muted">↗ </span>
            {span}
          </span>,
        );
        break;
      case "drawing":
        nodes.push(
          <span key={key} title={s.markDrawing} className="text-ink-muted italic">
            🖉 {span}
          </span>,
        );
        break;
      default:
        // Unknown annotation type — render text unchanged for now.
        nodes.push(span);
    }
    cursor = a.endOffset;
  });

  if (cursor < text.length) nodes.push(text.slice(cursor));

  return nodes.map((node, idx) =>
    typeof node === "string" ? (
      // biome-ignore lint/suspicious/noArrayIndexKey: derived purely from offsets
      <Fragment key={`${keyPrefix}-s${idx}`}>{node}</Fragment>
    ) : (
      node
    ),
  );
}
